import { useEffect, useRef, useState } from "react";
import { Phone, X } from "lucide-react";
import { toast } from "sonner";
import { api, getUtm, BUSINESS } from "@/lib/sleek";
import { fireConversion } from "@/lib/gtag";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SESSION_KEY = "sleek_exit_intent_shown";
const ARM_DELAY_MS = 5000;

export default function ExitIntentPopup({ abVariant, interest, suppress }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => { armed.current = true; }, ARM_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (suppress) setOpen(false);
  }, [suppress]);

  useEffect(() => {
    if (suppress) return;
    const onLeave = (e) => {
      if (!armed.current) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;
      if (e.clientY > 0) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [suppress]);

  // Mobile has no mouseleave — treat "scrolled down a good bit, then jumped back to the top
  // fast" as the closest signal to someone about to hit back/close the tab.
  useEffect(() => {
    if (suppress) return;
    let maxScrollY = 0;
    const onScroll = () => {
      if (!armed.current) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;
      const y = window.scrollY;
      maxScrollY = Math.max(maxScrollY, y);
      if (maxScrollY > 500 && y < 40) {
        sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [suppress]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        website: form.website,
        project_type: interest || "",
        message: "Submitted via exit-intent popup",
        source: "landing-exit-intent",
        ab_variant: abVariant,
        ...getUtm(),
      });
      fireConversion("lead");
      setDone(true);
    } catch {
      toast.error("Something went wrong — please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full rounded-none border border-zinc-300 bg-white text-zinc-950 placeholder:text-zinc-400 px-4 py-3 text-sm focus:outline-none focus:border-[#FF001E] focus:ring-1 focus:ring-[#FF001E] transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden border-none bg-zinc-950 text-white [&>button]:hidden" data-testid="exit-intent-popup">
        <div className="p-8 relative">
          <button onClick={() => setOpen(false)} className="absolute right-4 top-4 text-white/60 hover:text-white z-10" data-testid="exit-intent-close">
            <X size={18} />
          </button>
          {done ? (
            <div data-testid="exit-intent-success">
              <div className="font-display text-2xl font-light">You're in great hands.</div>
              <p className="text-white/70 mt-3 text-sm leading-relaxed">
                We've got your request and will reach out shortly. Want to talk sooner? Call{" "}
                <a href={BUSINESS.phoneHref} className="underline">{BUSINESS.phone}</a>.
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF001E]">Before you go</div>
                <DialogTitle className="font-display text-2xl font-light text-white mt-2 leading-tight">
                  Get your free design consultation.
                </DialogTitle>
                <DialogDescription className="text-white/70 text-sm mt-2">
                  No pressure, no obligation — just leave your name and email and we'll reach out to set up a time.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-5 space-y-3">
                <input aria-label="Name" className={field} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="exit-intent-input-name" />
                <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} data-testid="exit-intent-honeypot" />
                <input aria-label="Email" className={field} type="email" placeholder="Email address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="exit-intent-input-email" />
                <input aria-label="Phone" className={field} type="tel" placeholder="Phone number (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="exit-intent-input-phone" />
                <button type="submit" disabled={submitting} className="w-full bg-[#FF001E] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-[#D90019] transition-colors disabled:opacity-60" data-testid="exit-intent-submit">
                  {submitting ? "Sending…" : "Claim My Free Consultation"}
                </button>
                <a href={BUSINESS.phoneHref} className="flex items-center justify-center gap-2 text-white/60 text-xs pt-1 hover:text-white transition-colors" data-testid="exit-intent-call-link">
                  <Phone size={12} /> or call {BUSINESS.phone}
                </a>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
